import { useState, useRef, useEffect } from 'react';
import { Plus, CheckSquare, DollarSign, FileText, Calendar } from 'lucide-react';
import { clsx } from 'clsx';
import { useUIStore } from '@/stores';

const quickAddOptions = [
  { type: 'task', icon: CheckSquare, label: 'Task', color: 'text-blue-600 bg-blue-100 hover:bg-blue-200' },
  { type: 'transaction', icon: DollarSign, label: 'Transaction', color: 'text-green-600 bg-green-100 hover:bg-green-200' },
  { type: 'note', icon: FileText, label: 'Note', color: 'text-amber-600 bg-amber-100 hover:bg-amber-200' },
  { type: 'date', icon: Calendar, label: 'Date', color: 'text-purple-600 bg-purple-100 hover:bg-purple-200' },
] as const;

export function QuickAddButton() {
  const { openModal } = useUIStore();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (type: 'task' | 'transaction' | 'note' | 'date') => {
    setIsOpen(false);
    // Map 'date' to 'keyDate' for unified ItemType
    const itemType = type === 'date' ? 'keyDate' : type;
    openModal('itemForm', { defaultType: itemType });
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2" ref={menuRef}>
      {/* Options - horizontal row to the left of FAB */}
      {isOpen && (
        <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200">
          {quickAddOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.type}
                onClick={() => handleSelect(option.type)}
                className={clsx(
                  'w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all',
                  option.color
                )}
                title={option.label}
                aria-label={option.label}
              >
                <Icon className="h-5 w-5" />
              </button>
            );
          })}
        </div>
      )}

      {/* FAB button - stays as + */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all"
        aria-label="Quick add"
      >
        <Plus className="h-6 w-6 mx-auto" />
      </button>
    </div>
  );
}
